import { StoreCheckout } from "../../components/checkout/Checkout.types";
import { SubscriptionPlan } from "../../components/signup/SignUpApi";
import { isTrackingAllowed } from "./consent";

/**
 * Google Ads conversion tracking.
 *
 * `CustomHead` already loads `gtag.js` and calls `gtag('config', ...)`, which
 * is enough for page views — but a conversion is a separate `gtag('event',
 * 'conversion', ...)` call that nothing in this app was making. Google Ads
 * therefore recorded no purchases at all, so campaigns had nothing to optimise
 * against.
 *
 * A conversion needs two identifiers, and they come from different places in
 * the Google Ads UI:
 *   - the account's conversion ID, `AW-XXXXXXXXX` — the same value that loads
 *     the tag, already in `REACT_APP_GOOGLE_ADS_ID`
 *   - a per-action conversion label, issued when a conversion action is created
 *     under Goals → Conversions
 *
 * Both are required. Until the labels are configured every function here is a
 * no-op, so this is safe to ship before marketing has created the actions.
 */

const adsId = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const purchaseLabel = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || "";
const subscribeLabel = process.env.REACT_APP_GOOGLE_ADS_SUBSCRIBE_LABEL || "";
const signupLabel = process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL || "";

type Gtag = (command: string, event: string, params: Record<string, unknown>) => void;

const getGtag = (): Gtag | undefined => (window as any).gtag;

interface Conversion {
  label: string;
  value?: number;
  currency?: string;
  /** Google deduplicates on this, which is what makes a page refresh safe. */
  transactionId?: string;
}

const sendConversion = ({ label, value, currency, transactionId }: Conversion): void => {
  /* Same policy as the TikTok wrapper, from the same switch. Reading it here
     rather than trusting the call sites matters because `CustomHead` loads
     gtag whenever an ads id is configured and the signup and checkout flows
     call straight through — so if consent is ever reinstated, this is what
     stops order value, currency and transaction id going out regardless. */
  if (!isTrackingAllowed()) {
    return;
  }
  const gtag = getGtag();
  if (!adsId || !label || !gtag) {
    return;
  }
  /* Only the fields we actually know are sent. `trackCompleteRegistration`
     has no value or currency to give, and passing them through as `undefined`
     risks Google reading the value as 0 rather than falling back to the
     amount configured on the conversion action itself. */
  const params: Record<string, unknown> = { send_to: `${adsId}/${label}` };
  if (value != null) {
    params.value = value;
  }
  if (currency) {
    params.currency = currency;
  }
  if (transactionId) {
    params.transaction_id = transactionId;
  }
  gtag("event", "conversion", params);
};

class GoogleAdsAPI {
  trackPurchase(checkout: StoreCheckout) {
    const { amount, currency } = checkout?.totalPrice?.gross ?? {};
    sendConversion({
      label: purchaseLabel,
      value: amount,
      currency,
      /* The checkout id is stable across a refresh of the confirmation page,
         which is exactly the case Google's deduplication exists for. */
      transactionId: checkout?.id,
    });
  }

  trackSubscribe(plan: SubscriptionPlan) {
    const { amount, currency } = plan ?? {};
    sendConversion({ label: subscribeLabel, value: amount, currency });
  }

  trackCompleteRegistration() {
    sendConversion({ label: signupLabel });
  }
}

export default new GoogleAdsAPI();
